<template>
  <!-- MLB-Style Terms of Service Page -->
  <div class="bg-white dark:bg-slate-900 min-h-screen">
    
    <PageHeader
      title="Terms of Service"
      subtitle="Please read these terms carefully"
      centered
    />

    <!-- Content Section -->
    <div class="max-w-4xl mx-auto px-4 py-12">
      <div class="bg-white dark:bg-slate-800 rounded-lg shadow border border-gray-200 dark:border-slate-700 p-8">
        
        <!-- Last Updated -->
        <div class="mb-8 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
          <p class="text-sm text-blue-800 dark:text-blue-200">
            <span class="font-semibold">Last Updated:</span> {{ lastUpdated }}
          </p>
        </div>

        <!-- Introduction -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Agreement to Terms</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed mb-4">
            These Terms of Service ("Terms") govern your use of the Softball Stats Club application and website ("Service") operated by SDS Apps ("we," "our," or "us").
          </p>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed">
            By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of these terms, then you may not access the Service.
          </p>
        </section>

        <!-- Service Description -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Service Description</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed mb-4">
            Softball Stats Club is a web-based application that provides:
          </p>
          <ul class="list-disc list-inside text-gray-700 dark:text-slate-300 space-y-2">
            <li>Player and team statistics tracking</li>
            <li>Game score recording and analysis</li>
            <li>League standings and performance metrics</li>
            <li>User account management and authentication</li>
            <li>Data visualization and reporting tools</li>
          </ul>
        </section>

        <!-- User Accounts -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">User Accounts</h2>
          <h3 class="text-xl font-semibold text-gray-900 dark:text-slate-200 mb-3">Account Creation</h3>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed mb-4">
            To use certain features of our Service, you must create an account. You may create an account by:
          </p>
          <ul class="list-disc list-inside text-gray-700 dark:text-slate-300 mb-4 space-y-2">
            <li>Registering with a username and password</li>
            <li>Signing in with your Google account</li>
          </ul>

          <h3 class="text-xl font-semibold text-gray-900 dark:text-slate-200 mb-3">Account Responsibility</h3>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed mb-4">
            You are responsible for:
          </p>
          <ul class="list-disc list-inside text-gray-700 dark:text-slate-300 space-y-2">
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Providing accurate and up-to-date information</li>
            <li>Notifying us immediately of any unauthorized use</li>
          </ul>
        </section>

        <!-- Acceptable Use -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Acceptable Use</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed mb-4">
            You agree to use our Service only for lawful purposes and in accordance with these Terms. You agree not to:
          </p>
          <ul class="list-disc list-inside text-gray-700 dark:text-slate-300 space-y-2">
            <li>Use the Service for any illegal or unauthorized purpose</li>
            <li>Violate any applicable laws or regulations</li>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>Attempt to gain unauthorized access to any part of the Service</li>
            <li>Upload or transmit malicious code or content</li>
            <li>Harass, abuse, or harm other users</li>
            <li>Impersonate any person or entity</li>
            <li>Collect or harvest user information without consent</li>
          </ul>
        </section>

        <!-- Data and Content -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Data and Content</h2>
          <h3 class="text-xl font-semibold text-gray-900 dark:text-slate-200 mb-3">Your Data</h3>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed mb-4">
            You retain ownership of any data, statistics, or content you submit to the Service. By submitting content, you grant us a license to use, store, and display such content as necessary to provide the Service.
          </p>

          <h3 class="text-xl font-semibold text-gray-900 dark:text-slate-200 mb-3">Data Accuracy</h3>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed">
            While we strive to maintain accurate data, you acknowledge that statistics and information displayed may contain errors. We do not guarantee the accuracy, completeness, or reliability of any data.
          </p>
        </section>

        <!-- Privacy -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Privacy</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed">
            Your privacy is important to us. Please review our 
            <router-link to="/privacy-policy" class="text-mlb-navy dark:text-blue-300 hover:underline font-semibold">
              Privacy Policy
            </router-link>, 
            which also governs your use of the Service, to understand our practices.
          </p>
        </section>

        <!-- Service Availability -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Service Availability</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed">
            We strive to maintain the availability of our Service, but we do not guarantee uninterrupted access. The Service may be temporarily unavailable due to maintenance, updates, or technical issues. We reserve the right to modify, suspend, or discontinue the Service at any time.
          </p>
        </section>

        <!-- Intellectual Property -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Intellectual Property</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed mb-4">
            The Service and its original content, features, and functionality are owned by SDS Apps and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
          </p>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed">
            You may not reproduce, distribute, modify, or create derivative works of our Service without our express written permission.
          </p>
        </section>

        <!-- Disclaimer -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Disclaimer</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed">
            The Service is provided on an "AS IS" and "AS AVAILABLE" basis. We make no warranties, expressed or implied, regarding the Service's operation, availability, or accuracy. Your use of the Service is at your own risk.
          </p>
        </section>

        <!-- Limitation of Liability -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Limitation of Liability</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed">
            To the fullest extent permitted by law, SDS Apps shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses.
          </p>
        </section>

        <!-- Termination -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Termination</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed mb-4">
            We may terminate or suspend your account and access to the Service immediately, without prior notice, for any reason, including but not limited to breach of these Terms.
          </p>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed">
            You may terminate your account at any time by contacting us or using the account deletion features within the Service.
          </p>
        </section>

        <!-- Governing Law -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Governing Law</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed">
            These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which SDS Apps operates, without regard to conflict of law provisions.
          </p>
        </section>

        <!-- Contact Information -->
        <section class="mb-8">
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Contact Us</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed mb-4">
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div class="bg-gray-50 dark:bg-slate-700 p-4 rounded-lg">
            <p class="text-gray-700 dark:text-slate-300">
              <strong>Email:</strong> legal@sdsapps.com<br>
              <strong>Website:</strong> https://softball.sdsapps.com
            </p>
          </div>
        </section>

        <!-- Changes to Terms -->
        <section>
          <h2 class="text-2xl font-bold text-mlb-navy dark:text-blue-300 mb-4">Changes to These Terms</h2>
          <p class="text-gray-700 dark:text-slate-300 leading-relaxed">
            We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect. Your continued use of the Service after any changes constitutes acceptance of the new Terms.
          </p>
        </section>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import PageHeader from '@/components/common/PageHeader.vue'

// Last updated date
const lastUpdated = ref('January 8, 2025')
</script>